// ============================================================
// A WEAPON'S CONDITIONAL RULES (plan armoury, stage 3).
//
// "+2 damage and Tearing WHEN the target is Xenos." A rule is a name, a
// condition in the one condition language (`When`, `effectWhen`) and what
// it does to the attack while the condition holds:
//
//   - to hit         a line in the attack's modifiers, named for the rule;
//   - damage         added to the hit, as a stance's damage bonus is;
//   - damage dice, Pen, a gained special, a damage type
//                    merged into the WEAPON OVERRIDES the attack already
//                    applies (`applyWeaponOverrides`), so the roll, the
//                    forecast and the slot the trigger reads all agree.
//
// A rule may also lend its weapon a special's LOOK (`look`, a special's
// id): carried on the overrides as `looks`, written on the beat apart from
// the specials, so the display plays it while no mechanic reads it.
//
// Unknown is waiting, never applied - as for stance effects. Readers never
// learn where an override came from: a rule is only ever a patch.
//
// PER HIT: a clause about where a hit lands (`HIT_KINDS`) cannot be known
// at declaration. The declaration settles every other clause and hands the
// rest - a condition about the hit alone - to the roll as a `HitRule` on
// the overrides (so on the slot every damage path reads); `hitRuleBonuses`
// judges it per hit. Only the rule's damage and Pen ride it.
//
// Pure. Zero React dependency.
// ============================================================
import { holds, readWhen, whenSentence } from './character/effectWhen';
import type { Clause, Situation, When } from './character/effectWhen';
import { HIT_KINDS } from './character/whenClauses';
import { DAMAGE_TYPE_COLORS } from './constants';
import { mergeSpecials } from './weaponOverrides';
import type { WeaponOverrides } from '../types';

export type RuleEffect =
  | { type: 'to_hit'; value: number }
  | { type: 'damage'; value: number }
  | { type: 'damage_dice'; value: number }
  | { type: 'pen'; value: number }
  | { type: 'special'; rule: string }
  | { type: 'damage_type'; is: NonNullable<WeaponOverrides['damageTypeOverride']> };
export type RuleEffectType = RuleEffect['type'];

export interface WeaponRule {
  id: string;
  name: string;
  /** Absent: it always holds (while this weapon is the one swung). */
  when?: When;
  effects: RuleEffect[];
  /** A special's id whose look the attack borrows while the rule holds. */
  look?: string;
}

const MAX_BONUS = 60;
const DAMAGE_TYPES = Object.keys(DAMAGE_TYPE_COLORS) as NonNullable<WeaponOverrides['damageTypeOverride']>[];

/** A whole bonus, held to the table's range; null when it is no number or nothing at all. */
function bonus(value: unknown): number | null {
  const n = Math.trunc(Number(value));
  return Number.isFinite(n) && n !== 0 ? Math.max(-MAX_BONUS, Math.min(MAX_BONUS, n)) : null;
}

const READERS: Record<RuleEffectType, (raw: Record<string, unknown>) => RuleEffect | null> = {
  to_hit: (r) => (bonus(r.value) === null ? null : { type: 'to_hit', value: bonus(r.value)! }),
  damage: (r) => (bonus(r.value) === null ? null : { type: 'damage', value: bonus(r.value)! }),
  damage_dice: (r) => (bonus(r.value) === null ? null : { type: 'damage_dice', value: bonus(r.value)! }),
  pen: (r) => (bonus(r.value) === null ? null : { type: 'pen', value: bonus(r.value)! }),
  special: (r) => (typeof r.rule === 'string' && r.rule.trim() ? { type: 'special', rule: r.rule.trim() } : null),
  damage_type: (r) => {
    const is = DAMAGE_TYPES.find((t) => t === r.is);
    return is ? { type: 'damage_type', is } : null;
  },
};

function readEffect(raw: unknown): RuleEffect | null {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as Record<string, unknown>;
  const read = READERS[stored.type as RuleEffectType];
  return read ? read(stored) : null;
}

const text = (value: unknown, max: number): string => (typeof value === 'string' ? value.trim().slice(0, max) : '');

/** The optional parts of a stored rule, each only when it reads. */
function extrasOf(stored: Record<string, unknown>): Pick<WeaponRule, 'when' | 'look'> {
  const when = readWhen(stored.when);
  const look = text(stored.look, 40);
  return { ...(when ? { when } : {}), ...(look ? { look } : {}) };
}

function readRule(raw: unknown): WeaponRule | null {
  const stored = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const effects = Array.isArray(stored.effects) ? stored.effects.map(readEffect).filter((e) => e !== null) : [];
  if (typeof stored.id !== 'string' || effects.length === 0) return null;
  return { id: stored.id, name: text(stored.name, 60) || 'Rule', effects, ...extrasOf(stored) };
}

/** A weapon's rules off storage: what this build cannot apply is left out, and a rule with nothing left is gone. */
export function readWeaponRules(raw: unknown): WeaponRule[] {
  return Array.isArray(raw) ? raw.map(readRule).filter((r): r is WeaponRule => r !== null) : [];
}

/** A rule waiting on where each hit lands: its condition about the hit alone, and what it adds there. */
export interface HitRule {
  id: string;
  name: string;
  when: When;
  damage: number;
  pen: number;
}

/** What the rules that hold do to this attack. */
export interface SituatedRules {
  /** One line per rule with a to-hit effect, as the custom list holds them. */
  mods: { id: string; label: string; value: number; derived: true }[];
  /** Damage added to every hit. */
  damage: number;
  /** The table's overrides with the rules' patch merged in: the SAME object when no rule changes them. */
  overrides: WeaponOverrides;
  /** The names of the rules that hold, of those left to each hit, and of those that cannot be judged yet. */
  holding: string[];
  perHit: string[];
  waiting: string[];
}

/** One effect folded into the patch being built. */
const FOLD: { [T in RuleEffectType]: (o: WeaponOverrides, e: Extract<RuleEffect, { type: T }>) => WeaponOverrides } = {
  to_hit: (o) => o,
  damage: (o) => o,
  damage_dice: (o, e) => ({ ...o, damageDice: o.damageDice + e.value }),
  pen: (o, e) => ({ ...o, penDelta: o.penDelta + e.value }),
  special: (o, e) => ({ ...o, specials: mergeSpecials(o.specials, [e.rule]) }),
  damage_type: (o, e) => ({ ...o, damageTypeOverride: e.is }),
};

/** One effect of a holding rule: a line, damage, or a fold into the overrides. */
function foldEffect(out: SituatedRules, rule: WeaponRule, e: RuleEffect) {
  if (e.type === 'to_hit') out.mods.push({ id: `rule-${rule.id}`, label: saidOf(rule), value: e.value, derived: true });
  if (e.type === 'damage') out.damage += e.value;
  out.overrides = (FOLD[e.type] as (o: WeaponOverrides, e: RuleEffect) => WeaponOverrides)(out.overrides, e);
}

function foldRule(out: SituatedRules, rule: WeaponRule) {
  for (const e of rule.effects) foldEffect(out, rule, e);
  if (rule.look) out.overrides = { ...out.overrides, looks: [...(out.overrides.looks ?? []), rule.look] };
  out.holding.push(rule.name);
}

/** The ledger's label: the rule, and why it holds. */
const saidOf = (rule: WeaponRule) => (rule.when ? `${rule.name}: ${whenSentence(rule.when)}` : rule.name);

/** Does it hold? With no weapon swung it waits; with no condition it always holds. */
function verdictOf(rule: WeaponRule, situation: Situation): boolean | null {
  if (!situation.attack) return null;
  return rule.when ? holds(rule.when, situation) : true;
}

const isHitClause = (c: Clause) => HIT_KINDS.includes(c.kind);
const hasHitClause = (when: When | undefined) => !!when?.some((group) => group.some(isHitClause));

/**
 * A condition with clauses about the hit, judged as far as the declaration can: a group whose other clauses are
 * false or unknown is dropped; one whose other clauses all hold keeps only its hit clauses. `now` when a group with
 * no hit clause holds outright.
 */
function residualOf(when: When, situation: Situation): { now: boolean; rest: When } {
  const rest: When = [];
  for (const group of when) {
    const known = group.filter((c) => !isHitClause(c));
    if (known.length && holds([known], situation) !== true) continue;
    const hit = group.filter(isHitClause);
    if (!hit.length) return { now: true, rest: [] };
    rest.push(hit);
  }
  return { now: false, rest };
}

/** The damage and Pen a rule adds to each hit its condition holds for. */
function hitRuleOf(rule: WeaponRule, when: When): HitRule {
  const sum = (type: 'damage' | 'pen') =>
    rule.effects.reduce((n, e) => n + (e.type === type ? (e as { value: number }).value : 0), 0);
  return { id: rule.id, name: rule.name, when, damage: sum('damage'), pen: sum('pen') };
}

/** A rule with clauses about the hit: folded now if the declaration settles it, else handed to each hit. */
function situateHitRule(out: SituatedRules, rule: WeaponRule, situation: Situation) {
  const { now, rest } = residualOf(rule.when!, situation);
  if (now) return foldRule(out, rule);
  if (!rest.length) return;
  out.overrides = { ...out.overrides, hitRules: [...(out.overrides.hitRules ?? []), hitRuleOf(rule, rest)] };
  out.perHit.push(rule.name);
}

/**
 * Judge each rule against the situation and fold in those that hold.
 *
 * @param base - the overrides the table has set (or the empty ones)
 */
export function situateRules(rules: readonly WeaponRule[], situation: Situation, base: WeaponOverrides): SituatedRules {
  const out: SituatedRules = { mods: [], damage: 0, overrides: base, holding: [], perHit: [], waiting: [] };
  for (const rule of rules) situateRule(out, rule, situation);
  return out;
}

/** One rule: left partly to the hits when it asks where they land, else held, waiting or not. */
function situateRule(out: SituatedRules, rule: WeaponRule, situation: Situation) {
  if (situation.attack && hasHitClause(rule.when)) return situateHitRule(out, rule, situation);
  const verdict = verdictOf(rule, situation);
  if (verdict === null) out.waiting.push(rule.name);
  if (verdict) foldRule(out, rule);
}

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));
const SAY: { [T in RuleEffectType]: (e: Extract<RuleEffect, { type: T }>) => string } = {
  to_hit: (e) => `${signed(e.value)} to hit`,
  damage: (e) => `${signed(e.value)} damage`,
  damage_dice: (e) => `${signed(e.value)}d10 damage`,
  pen: (e) => `${signed(e.value)} Pen`,
  special: (e) => `gains ${e.rule}`,
  damage_type: (e) => `deals ${e.is} damage`,
};

/** The rule in words: "Xenos-bane: +10 to hit, gains Tearing - when the target is Xenos". */
export function ruleSentence(rule: WeaponRule): string {
  const does = rule.effects.map((e) => (SAY[e.type] as (e: RuleEffect) => string)(e)).join(', ');
  return `${rule.name}: ${does}${rule.when ? ` - when ${whenSentence(rule.when)}` : ''}`;
}

/**
 * What the rules left to the hits add to THIS hit, where it landed and the worn armour there (`rawDamageOf`).
 * Nothing at all with none: every existing hit reads as it did.
 */
export function hitRuleBonuses(
  rules: readonly HitRule[] | undefined,
  hit: { location: string; armour: number },
): { dmg: number; pen: number } {
  const out = { dmg: 0, pen: 0 };
  for (const rule of rules ?? []) {
    if (holds(rule.when, { hit }) !== true) continue;
    out.dmg += rule.damage;
    out.pen += rule.pen;
  }
  return out;
}
